import React from 'react'
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd'
import { Box } from '@mui/material'
import SingleOption from '../SingleOption'

interface ItemProps {
    id?: string | number
    answerId?: string | number
    content: string
    correct: boolean
}

interface OptionsDndProps {
    setItems: (elements: any[]) => void
    handleDelete: (id: number | string) => void
    handleBadgeClick: (index: number) => void
    formik: any
    questionIndex: number
}

const reorder = (list: ItemProps[], startIndex: number, endIndex: number) => {
    const result = Array.from(list)
    const [removed] = result.splice(startIndex, 1)

    result.splice(endIndex, 0, removed)

    return result
}

function Item({
    item,
    index,
    handleDelete,
    handleBadgeClick,
    formik,
    questionIndex,
}: {
    item: ItemProps
    index: number
    handleDelete: (id: number | string) => void
    handleBadgeClick: (index: number) => void
    formik: any
    questionIndex: number
}) {
    return (
        <Draggable
            draggableId={`answer-${item.id || item.answerId}`}
            index={index}
        >
            {(provided, snapshot) => (
                <SingleOption
                    item={item}
                    formik={formik}
                    questionIndex={questionIndex}
                    innerRef={provided.innerRef}
                    snapshot={snapshot}
                    draggableProps={provided.draggableProps}
                    dragHandleProps={provided.dragHandleProps}
                    index={index}
                    key={item.id}
                    handleDelete={handleDelete}
                    handleBadgeClick={handleBadgeClick}
                />
            )}
        </Draggable>
    )
}

const ItemList = React.memo(function ItemList({
    formik,
    questionIndex,
    handleBadgeClick,
    handleDelete,
}: any) {
    return formik.values.questions[questionIndex].answers.map(
        (item: ItemProps, index: number) => (
            <Item
                item={item}
                formik={formik}
                questionIndex={questionIndex}
                index={index}
                key={`answer-${item.id || item.answerId}`}
                handleDelete={handleDelete}
                handleBadgeClick={handleBadgeClick}
            />
        ),
    )
})

function OptionsDnd({
    setItems,
    handleDelete,
    formik,
    questionIndex,
    handleBadgeClick,
}: OptionsDndProps) {
    function onDragEnd(result) {
        if (!result.destination) {
            return
        }

        if (result.destination.index === result.source.index) {
            return
        }

        const newItems = reorder(
            formik.values.questions[questionIndex].answers,
            result.source.index,
            result.destination.index,
        )

        setItems(newItems)
    }

    return (
        <DragDropContext onDragEnd={onDragEnd}>
            <Droppable droppableId="list">
                {(provided) => (
                    <Box
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                    >
                        <ItemList
                            handleDelete={handleDelete}
                            handleBadgeClick={handleBadgeClick}
                            formik={formik}
                            questionIndex={questionIndex}
                        />
                        {provided.placeholder}
                    </Box>
                )}
            </Droppable>
        </DragDropContext>
    )
}

export default OptionsDnd
